// Lightweight i18n: a flat dictionary per language (KISS). Language is derived
// from the route prefix (useLang).

export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    version: string;
    v4Hint: string;
    v7Hint: string;
    count: string;
    uppercase: string;
    hyphens: string;
    generate: string;
    copyAll: string;
    copied: string;
    output: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'UUID-Generator',
    subtitle:
      'Erzeuge UUIDs (v4 und v7) direkt im Browser — einzeln oder als Liste. Nichts wird gesendet oder gespeichert.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    version: 'Version',
    v4Hint: 'v4 — vollständig zufällig. Der gängige Standard.',
    v7Hint: 'v7 — zeitlich sortierbar (Zeitstempel + Zufall).',
    count: 'Anzahl',
    uppercase: 'Großbuchstaben',
    hyphens: 'Mit Bindestrichen',
    generate: 'Neu generieren',
    copyAll: 'Alle kopieren',
    copied: 'Kopiert',
    output: 'Ergebnis',
  },
  seo: {
    aboutTitle: 'UUIDs sicher und lokal erzeugen',
    aboutBody:
      'Dieser Generator erzeugt UUIDs ausschließlich lokal in deinem Browser über die Web Crypto API. Es werden keine Daten an einen Server gesendet oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Was ist der Unterschied zwischen v4 und v7?',
        a: 'v4 ist vollständig zufällig. v7 stellt einen Zeitstempel voran und ist dadurch zeitlich sortierbar — praktisch für Datenbank-Schlüssel.',
      },
      {
        q: 'Sind die UUIDs kryptografisch zufällig?',
        a: 'Ja. Die Zufallsbits stammen aus der Web Crypto API (crypto.randomUUID bzw. getRandomValues).',
      },
      {
        q: 'Werden die Werte gespeichert?',
        a: 'Nein. Alles läuft lokal im Browser; nichts wird übertragen.',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Alles läuft lokal im Browser.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'UUID Generator',
    subtitle:
      'Generate UUIDs (v4 and v7) right in your browser — one or many at once. Nothing is sent or stored.',
    localBadge: '100% local · free',
  },
  ui: {
    version: 'Version',
    v4Hint: 'v4 — fully random. The common standard.',
    v7Hint: 'v7 — time-sortable (timestamp + random).',
    count: 'Count',
    uppercase: 'Uppercase',
    hyphens: 'With hyphens',
    generate: 'Regenerate',
    copyAll: 'Copy all',
    copied: 'Copied',
    output: 'Result',
  },
  seo: {
    aboutTitle: 'Generate UUIDs securely and locally',
    aboutBody:
      'This generator creates UUIDs exclusively in your browser via the Web Crypto API. No data is sent to a server or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'What is the difference between v4 and v7?',
        a: 'v4 is fully random. v7 prefixes a timestamp and is therefore time-sortable — handy for database keys.',
      },
      {
        q: 'Are the UUIDs cryptographically random?',
        a: 'Yes. Random bits come from the Web Crypto API (crypto.randomUUID and getRandomValues).',
      },
      {
        q: 'Are the values stored?',
        a: 'No. Everything runs locally in your browser; nothing is transmitted.',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Everything runs locally in your browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
